import React, { useState } from 'react';
import { Modal, Card, Button } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Customer, NewCustomerModalProps, Website } from './CustomerInterfaces';
import { CONTACT_TYPES } from '../../constants/contactTypes';
import { doc, setDoc, collection, deleteDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import { AdsAccountPicker } from '../marketing/AdsAccountPicker';

// Helper function to convert various date formats to YYYY-MM-DD string for date inputs
const toDateInputValue = (value: any): string => {
  if (!value) return '';
  
  // Handle Firestore Timestamp objects (have seconds and nanoseconds)
  if (value && typeof value === 'object' && 'seconds' in value) {
    const date = new Date(value.seconds * 1000);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split('T')[0];
    }
    return '';
  }
  
  // Handle Date objects
  if (value instanceof Date) {
    if (!isNaN(value.getTime())) {
      return value.toISOString().split('T')[0];
    }
    return '';
  }
  
  // Handle string dates
  if (typeof value === 'string') {
    // Already in YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }
    // Try to parse and convert
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split('T')[0];
    }
  }
  
  return '';
};

const NewCustomerModal: React.FC<NewCustomerModalProps> = ({
  show,
  onClose,
  customer,
  onDeleted,
  deleteSummary,
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Form validation schema
  const customerSchema = Yup.object().shape({
    name: Yup.string().required('Full name is required'),
    email: Yup.string().email('Invalid email').required('Email is required'),
    phone: Yup.string().required('Phone number is required'),
    address: Yup.string().required('Physical address is required'),
    company: Yup.string().nullable(),
    industry: Yup.string().nullable(),
    notes: Yup.string().nullable(),
    createdAt: Yup.date().required('Creation date is required'),
    lastContacted: Yup.date().nullable(),
    contacts: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().nullable(),
        email: Yup.string().email('Invalid email').nullable(),
        phone: Yup.string().nullable()
      })
    ),
    contactType: Yup.string().nullable(),
    referralSource: Yup.string().oneOf(['Google Ads', 'Organic Search', 'Word Of Mouth', 'Repeat Customer']).nullable(),
    proposalReceived: Yup.date().nullable(),
    googleReviewStars: Yup.number().min(1).max(5).nullable(),
    googleReviewMessage: Yup.string().nullable()
  });

  // Define the initial values
  const initialValues: Customer = customer ? { 
    ...customer, 
    contacts: customer.contacts || [], 
    websites: customer.websites || [],
    // Convert date fields to proper YYYY-MM-DD format for date inputs
    createdAt: toDateInputValue(customer.createdAt),
    lastContacted: toDateInputValue(customer.lastContacted),
    proposalReceived: toDateInputValue(customer.proposalReceived),
  } : {
    id: '',
    contacts: [],
    websites: [],
    name: '',
    email: '',
    phone: '',
    address: '',
    company: '',
    industry: '',
    notes: '',
    createdAt: new Date().toISOString().split('T')[0],
    lastContacted: new Date().toISOString().split('T')[0],
    contactType: '',
    referralSource: undefined,
    proposalReceived: undefined,
    googleReviewStars: undefined,
    googleReviewMessage: '',
    googleAdsCustomerId: '',
    googleAdsCustomerName: '',
    googleAdsBoundAt: '',
  };

  // useFormik hook
  const formik = useFormik({
    initialValues,
    validationSchema: customerSchema,
    onSubmit: async (values) => {
      try {
        // Clean up the data by removing undefined values and converting them to null for Firestore
        const cleanedValues = Object.entries(values).reduce<Partial<Customer>>((acc, [key, value]) => ({
          ...acc,
          [key]: value === undefined ? null : value
        }), {});
    
        if (customer && customer.id) {
          // Update existing document
          await setDoc(doc(db, 'customers', customer.id), cleanedValues);
          console.log('Customer updated successfully');
        } else {
          // Create new document
          const { id, ...customerWithoutId } = cleanedValues;
          await setDoc(doc(collection(db, 'customers')), customerWithoutId);
          console.log('New Customer added successfully');
        }
        onClose();
      } catch (error) {
        console.error('Error adding new customer: ', error);
      }
    },
    enableReinitialize: true,
  });

  const handleDelete = async () => {
    if (!customer?.id) return;

    try {
      setIsDeleting(true);
      await deleteDoc(doc(db, 'customers', customer.id));
      setShowDeleteConfirm(false);
      onDeleted?.(customer.id);
      onClose();
    } catch (error) {
      console.error('Error deleting customer: ', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleModalClose = () => {
    setShowDeleteConfirm(false);
    onClose();
  };

  const isBusy = formik.isSubmitting || isDeleting;
  const linkedJobs = deleteSummary?.jobs ?? 0;
  const linkedInvoices = deleteSummary?.invoices ?? 0;
  const linkedEvents = deleteSummary?.events ?? 0;
  const linkedRecordCount = linkedJobs + linkedInvoices + linkedEvents;

  return (
    <>
    <Modal show={show} onHide={handleModalClose} centered size="xl">
      <Modal.Header closeButton>
        <Modal.Title>{customer ? 'Edit Contact' : 'New Contact'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Card>
          <Card.Body>
            <form onSubmit={formik.handleSubmit}>

              {/* Primary Contact and Email */}
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="name" className="form-label">Primary Contact</label>
                    <input type="text" className="form-control" id="name" {...formik.getFieldProps('name')} />
                    {formik.touched.name && formik.errors.name && <div className="text-danger">{formik.errors.name}</div>}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="email" className="form-label">Primary Email</label>
                    <input type="email" className="form-control" id="email" {...formik.getFieldProps('email')} />
                    {formik.touched.email && formik.errors.email && <div className="text-danger">{formik.errors.email}</div>}
                  </div>
                </div>
              </div>

              {/* Contact Type and Referral Source */}
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="contactType" className="form-label">Contact Type</label>
                    <select className="form-select" id="contactType" {...formik.getFieldProps('contactType')}>
                      <option value="">Select Type</option>
                      {CONTACT_TYPES.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                    {formik.touched.contactType && formik.errors.contactType && 
                      <div className="text-danger">{formik.errors.contactType}</div>}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="referralSource" className="form-label">Referral Source</label>
                    <select className="form-select" id="referralSource" {...formik.getFieldProps('referralSource')}>
                      <option value="">Select Source</option>
                      <option value="Google Ads">Google Ads</option>
                      <option value="Organic Search">Organic Search</option>
                      <option value="Word Of Mouth">Word Of Mouth</option>
                      <option value="Repeat Customer">Repeat Customer</option>
                    </select>
                    {formik.touched.referralSource && formik.errors.referralSource &&
                      <div className="text-danger">{formik.errors.referralSource}</div>}
                  </div>
                </div>
              </div>

              {/* Google Ads binding — optional 1:1 link to an Ads account.
                  When set, the customer detail panel surfaces the bound
                  account's KPIs / campaigns and the true-ROAS widget
                  cross-joins paid invoices with Ads spend. */}
              <div className="row">
                <div className="col-md-12">
                  <div className="mb-3">
                    <label className="form-label">Google Ads Account</label>
                    <AdsAccountPicker
                      value={formik.values.googleAdsCustomerId}
                      onChange={(cid, name) => {
                        formik.setFieldValue('googleAdsCustomerId', cid);
                        formik.setFieldValue('googleAdsCustomerName', name);
                        // Stamp the bind time only when a real cid is
                        // picked; clearing it resets the stamp too so the
                        // health-check banner doesn't lie.
                        formik.setFieldValue('googleAdsBoundAt', cid ? new Date().toISOString() : '');
                      }}
                      placeholder="— no Google Ads account bound —"
                    />
                  </div>
                </div>
              </div>

              {/* Secondary Contacts */}
              {formik.values.contacts && formik.values.contacts.map((contact, index) => (
                <div key={index}>
                  <hr style={{borderTop: '1px dashed var(--bs-border-color)'}} />
                  <div className="row">
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label htmlFor={`contacts[${index}].name`} className="form-label">Secondary Contact Name</label>
                        <input type="text" className="form-control" id={`contacts[${index}].name`} {...formik.getFieldProps(`contacts[${index}].name`)} />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label htmlFor={`contacts[${index}].email`} className="form-label">Secondary Contact Email</label>
                        <input type="email" className="form-control" id={`contacts[${index}].email`} {...formik.getFieldProps(`contacts[${index}].email`)} />
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="mb-3">
                        <label htmlFor={`contacts[${index}].phone`} className="form-label">Secondary Contact Phone</label>
                        <input type="text" className="form-control" id={`contacts[${index}].phone`} {...formik.getFieldProps(`contacts[${index}].phone`)} />
                      </div>
                    </div>
                    <div className="col-md-1">
                      <div className="mb-3 d-flex align-items-end h-100">
                        <button type="button" className="btn btn-link p-0" onClick={() => {
                          const newContacts = [...formik.values.contacts];
                          newContacts.splice(index, 1);
                          formik.setFieldValue('contacts', newContacts);
                        }}>
                          <i className="bi bi-trash fs-3"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                  <hr style={{borderTop: '1px dashed var(--bs-border-color)'}} />
                </div>
              ))}

              <Button 
                className='btn Primary' 
                style={{ marginTop: '20px', marginBottom: '20px' }} 
                onClick={() => formik.setFieldValue('contacts', [...formik.values.contacts, { name: '', email: '', phone: '' }])}
              >
                Add Secondary Contact
              </Button>

              {/* Websites (placed directly under Secondary Contacts) */}
              {Array.isArray(formik.values.websites) && formik.values.websites.map((site: Website, index: number) => (
                <div key={index}>
                  <hr style={{borderTop: '1px dashed var(--bs-border-color)'}} />
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor={`websites[${index}].description`} className="form-label">Website Description</label>
                        <input type="text" className="form-control" id={`websites[${index}].description`} {...formik.getFieldProps(`websites[${index}].description`)} />
                      </div>
                    </div>
                    <div className="col-md-5">
                      <div className="mb-3">
                        <label htmlFor={`websites[${index}].url`} className="form-label">Website URL</label>
                        <input 
                          type="url" 
                          className="form-control" 
                          id={`websites[${index}].url`} 
                          placeholder="https://example.com" 
                          {...formik.getFieldProps(`websites[${index}].url`)}
                          onBlur={(e) => {
                            const v = (e.target.value || '').trim();
                            if (v && !/^https?:\/\//i.test(v)) {
                              const updated = [...(formik.values.websites || [])];
                              updated[index] = { ...(updated[index] || {}), url: `https://${v}` } as Website;
                              formik.setFieldValue('websites', updated, false);
                            }
                          }}
                        />
                      </div>
                    </div>
                    <div className="col-md-1">
                      <div className="mb-3 d-flex align-items-end h-100">
                        <button type="button" className="btn btn-link p-0" onClick={() => {
                          const updated = [...(formik.values.websites || [])];
                          updated.splice(index, 1);
                          formik.setFieldValue('websites', updated);
                        }}>
                          <i className="bi bi-trash fs-3"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                  <hr style={{borderTop: '1px dashed var(--bs-border-color)'}} />
                </div>
              ))}
              <br></br>

              <Button 
                className='btn Primary' 
                style={{ marginTop: '10px', marginBottom: '20px' }} 
                onClick={() => formik.setFieldValue('websites', [...(formik.values.websites || []), { description: '', url: '' }])}
              >
                Add Website
              </Button>

              {/* Phone and Address */}
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="phone" className="form-label">Phone Number</label>
                    <input type="text" className="form-control" id="phone" {...formik.getFieldProps('phone')} />
                    {formik.touched.phone && formik.errors.phone && <div className="text-danger">{formik.errors.phone}</div>}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="address" className="form-label">Address</label>
                    <input type="text" className="form-control" id="address" {...formik.getFieldProps('address')} />
                    {formik.touched.address && formik.errors.address && <div className="text-danger">{formik.errors.address}</div>}
                  </div>
                </div>
              </div>

              {/* Company and Industry */}
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="company" className="form-label">Company</label>
                    <input type="text" className="form-control" id="company" {...formik.getFieldProps('company')} />
                    {formik.touched.company && formik.errors.company && <div className="text-danger">{formik.errors.company}</div>}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="industry" className="form-label">Industry</label>
                    <input type="text" className="form-control" id="industry" {...formik.getFieldProps('industry')} />
                    {formik.touched.industry && formik.errors.industry && <div className="text-danger">{formik.errors.industry}</div>}
                  </div>
                </div>
              </div>

              {/* Proposal Received Date */}
              <div className="mb-3">
                <label htmlFor="proposalReceived" className="form-label">Proposal Received Date</label>
                <input 
                  type="date" 
                  className="form-control" 
                  id="proposalReceived" 
                  {...formik.getFieldProps('proposalReceived')} 
                />
                {formik.touched.proposalReceived && formik.errors.proposalReceived && 
                  <div className="text-danger">{formik.errors.proposalReceived}</div>}
              </div>

              {/* Google Review Section */}
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="googleReviewStars" className="form-label">Google Review Rating</label>
                    <select 
                      className="form-select" 
                      id="googleReviewStars" 
                      {...formik.getFieldProps('googleReviewStars')}
                    >
                      <option value="">Select Rating</option>
                      {[1, 2, 3, 4, 5].map(rating => (
                        <option key={rating} value={rating}>{rating} Stars</option>
                      ))}
                    </select>
                    {formik.touched.googleReviewStars && formik.errors.googleReviewStars && 
                      <div className="text-danger">{formik.errors.googleReviewStars}</div>}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="googleReviewMessage" className="form-label">Google Review Message</label>
                    <textarea 
                      className="form-control" 
                      id="googleReviewMessage" 
                      {...formik.getFieldProps('googleReviewMessage')}
                    ></textarea>
                    {formik.touched.googleReviewMessage && formik.errors.googleReviewMessage && 
                      <div className="text-danger">{formik.errors.googleReviewMessage}</div>}
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="mb-3">
                <label htmlFor="notes" className="form-label">Notes</label>
                <textarea 
                  className="form-control" 
                  id="notes" 
                  {...formik.getFieldProps('notes')}
                ></textarea>
                {formik.touched.notes && formik.errors.notes && 
                  <div className="text-danger">{formik.errors.notes}</div>}
              </div>

              {/* Dates */}
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="createdAt" className="form-label">Created At</label>
                    <input 
                      type="date" 
                      className="form-control" 
                      id="createdAt" 
                      {...formik.getFieldProps('createdAt')} 
                    />
                    {formik.touched.createdAt && formik.errors.createdAt && 
                      <div className="text-danger">{formik.errors.createdAt}</div>}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="lastContacted" className="form-label">Last Contacted</label>
                    <input 
                      type="date" 
                      className="form-control" 
                      id="lastContacted" 
                      {...formik.getFieldProps('lastContacted')} 
                    />
                    {formik.touched.lastContacted && formik.errors.lastContacted && 
                      <div className="text-danger">{formik.errors.lastContacted}</div>}
                  </div>
                </div>
              </div>

              <div className="d-flex justify-content-between align-items-center mt-5">
                <div>
                  {customer?.id && (
                    <Button
                      type="button"
                      variant="danger"
                      onClick={() => setShowDeleteConfirm(true)}
                      disabled={isBusy}
                    >
                      Delete Contact
                    </Button>
                  )}
                </div>
                <Button type="submit" variant="primary" disabled={isBusy}>
                  {formik.isSubmitting ? 'Saving...' : customer ? 'Save Changes' : 'Save Contact'}
                </Button>
              </div>
            </form>
          </Card.Body>
        </Card>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleModalClose} disabled={isBusy}>Close</Button>
      </Modal.Footer>
    </Modal>

    <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Delete Contact</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex align-items-start">
          <i className="bi bi-exclamation-triangle-fill text-danger fs-1 me-4"></i>
          <div>
            <div className="fw-bold mb-2">
              Delete {customer?.company || customer?.name || 'this contact'}?
            </div>
            <div className="text-muted mb-3">
              This action cannot be undone.
            </div>
            {linkedRecordCount > 0 && (
              <div className="alert alert-warning py-3 mb-0">
                This contact is linked to {linkedJobs} job{linkedJobs !== 1 ? 's' : ''}, {linkedInvoices} invoice{linkedInvoices !== 1 ? 's' : ''}, and {linkedEvents} event{linkedEvents !== 1 ? 's' : ''}. Deleting the contact will not automatically remove or relink those records.
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={() => setShowDeleteConfirm(false)} disabled={isBusy}>
          Cancel
        </Button>
        <Button variant="danger" onClick={handleDelete} disabled={isBusy}>
          {isDeleting ? 'Deleting...' : 'Delete Contact'}
        </Button>
      </Modal.Footer>
    </Modal>
    </>
  );
};

export default NewCustomerModal;